import { useCallback } from 'react'
import { AggregateApi } from '@/api/models/aggregate'
import { useAuth } from '@/features/auth/useAuth'
import { getOpenCatalogObjectiveIdParam } from './openCatalogCourseFlow'

const RETRY_CONFIG = {
  maxAttempts: 15,
  delayMs: 1500,
} as const

const createObjectiveFilter = (objectiveId: string, userId?: string): string => {
  if (!userId) return '[]'

  return JSON.stringify([['objective.id', '=', objectiveId], 'and', ['userId', '=', userId]])
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

export const useWaitForObjectiveWorkflowAggregate = () => {
  const { user } = useAuth()
  const objectiveId = getOpenCatalogObjectiveIdParam()

  const fetchAggregate = useCallback(async () => {
    const filter = createObjectiveFilter(objectiveId, user?.id)

    for (let attempt = 0; attempt < RETRY_CONFIG.maxAttempts; attempt++) {
      if (attempt > 0) {
        await delay(RETRY_CONFIG.delayMs)
      }

      try {
        const [aggregate] = await AggregateApi.getObjectiveWorkflowAggregatesWithQuery({ filter })
        if (aggregate) return aggregate
      } catch (error) {
        console.error('Failed to fetch aggregate:', error)
      }
    }

    throw new Error(`Aggregate not found after ${RETRY_CONFIG.maxAttempts} attempts`)
  }, [objectiveId, user?.id])

  return { waitForObjectiveWorkflowAggregate: fetchAggregate }
}
